import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Send, MessageCircle, Clock } from 'lucide-react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { SiteFiling } from '../../components/common/SiteFiling';
import { post } from '../../api/client';

// 咨询类型下拉的 value 是英文码，服务端 consult_type 存的是中文标签（直接拼进留言正文展示）。
const CONSULT_TYPE_LABELS: Record<string, string> = {
  product: '产品咨询',
  technical: '技术支持',
  business: '商务合作',
  other: '其他问题',
};

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  company: '',
  type: '',
  message: '',
  // 蜜罐：真人不可见恒为空串，脚本填了服务端即静默丢弃。字段名与 DTO website 一致，
  // 走请求体 camelCase→snake 转换后仍是 website。
  website: '',
};

export default function ContactPage() {
  const { siteName, logoUrl, contactEmail, contactPhone, contactHours, address } = useSiteConfig();
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // 监听滚动事件，控制导航栏样式
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      // 空的可选项发 undefined：JSON.stringify 会丢键，避开后端 @IsOptional 对空串的邮箱校验误判。
      await post('/customer-service/messages', {
        customerName: formData.name.trim(),
        customerPhone: formData.phone.trim(),
        customerEmail: formData.email.trim() || undefined,
        message: formData.message.trim(),
        consultType: CONSULT_TYPE_LABELS[formData.type] || undefined,
        company: formData.company.trim() || undefined,
        website: formData.website,
      });
      setSubmitSuccess(true);
      setFormData({ ...EMPTY_FORM });
      // 5 秒后收起成功提示，页面留得住、也提醒后续还会回复
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      // client 已把后端中文 message（含 429「请求过于频繁」）翻成 Error
      setSubmitError(error instanceof Error ? error.message : '提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* 顶部导航 */}
      <nav className={`fixed top-0 left-0 right-0 z-50 h-[70px] transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-100' : 'bg-transparent'}`}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 h-full flex items-center justify-between">
          <a href="/login" className="flex items-center gap-3">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="w-10 h-10"
            />
            <span className={`text-lg font-bold tracking-wide transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'}`}>
              {siteName}
            </span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/help" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              帮助中心
            </a>
            <a href="/about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              关于我们
            </a>
            <a href="/contact" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-teal-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              联系我们
            </a>
            <a 
              href="/login" 
              className={`text-sm font-medium px-5 py-2 rounded-full transition-all ${isScrolled ? 'bg-teal-600 text-white hover:bg-teal-700' : 'border border-white/70 text-white hover:bg-white/20'}`}
            >
              立即登录
            </a>
          </div>
        </div>
      </nav>

      {/* Hero 顶部区域 */}
      <section className="pt-[140px] pb-16 bg-gradient-to-br from-slate-800 via-slate-700 to-teal-800">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          {/* 标题 */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            联系我们
          </h1>

          {/* 描述文字 */}
          <p className="max-w-xl mx-auto text-sm md:text-base text-slate-300 leading-relaxed mb-6">
            无论是产品咨询、技术支持还是商务合作，我们都很乐意听到您的声音。
          </p>

          {/* 联系按钮 */}
          <a href="#contact-form" className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-full transition-colors">
            <MessageCircle className="h-4 w-4" />
            <span>立即联系我们</span>
          </a>
        </div>
      </section>

      {/* 主内容区 */}
      <main className="flex-1 py-14">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          {/* 面包屑导航 */}
          <div className="text-xs text-slate-500 mb-8">
            <a href="/" className="hover:text-teal-600 transition-colors">首页</a>
            <span className="mx-2">/</span>
            <span className="text-slate-700 font-semibold">联系我们</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* 联系信息 */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <h2 className="text-xl font-bold text-slate-800 mb-6">快速找到我们</h2>
              <p className="text-sm text-slate-600 mb-6">
                选择您喜欢的联系方式，我们将在 7 个工作日内回复。
              </p>
              
              <div className="space-y-6">
                {contactEmail && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                      <Mail className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-1">技术支持邮箱</h3>
                      <p className="text-slate-600 text-sm">
                        <a href={`mailto:${contactEmail}`} className="hover:text-teal-600 transition-colors">
                          {contactEmail}
                        </a>
                      </p>
                    </div>
                  </div>
                )}

                {contactPhone && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                      <Phone className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-1">客服电话</h3>
                      <p className="text-slate-600 text-sm">
                        <a href={`tel:${contactPhone}`} className="hover:text-teal-600 transition-colors">
                          {contactPhone}
                        </a>
                      </p>
                    </div>
                  </div>
                )}
                
                {contactHours && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                      <Clock className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-1">工作时间</h3>
                      <p className="text-slate-600 text-sm">
                        {contactHours}
                      </p>
                    </div>
                  </div>
                )}
                
                {address && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                      <MapPin className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-1">公司地址</h3>
                      <p className="text-slate-600 text-sm">{address}</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-8 p-4 bg-teal-50 border border-teal-100 rounded-lg">
                <h3 className="text-sm font-semibold text-teal-800 mb-2">还在寻找答案？</h3>
                <p className="text-xs text-teal-700 mb-3">
                  80%的问题可以在我们的帮助中心找到答案，节省您的时间。
                </p>
                <a href="/help" className="text-xs text-teal-600 hover:text-teal-700 transition-colors flex items-center gap-1">
                  <span>前往帮助中心 →</span>
                </a>
              </div>
            </div>
            
            {/* 联系表单 */}
            <div id="contact-form" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <h2 className="text-xl font-bold text-slate-800 mb-6">发送消息</h2>
              <p className="text-sm text-slate-600 mb-6">
                所有标 * 的字段为必填项
              </p>
              
              {submitSuccess ? (
                <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-100 rounded-lg mb-6">
                  <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm text-green-700">
                    消息发送成功！我们将在 7 个工作日内回复您。
                  </p>
                </div>
              ) : null}

              {submitError ? (
                <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-lg mb-6">
                  <svg className="h-5 w-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                  </svg>
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              ) : null}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* 蜜罐：移出可视区但保留在 DOM，脚本会连它一起填；真人看不见也 Tab 不到 */}
                <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                  <label htmlFor="website">请勿填写此项</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      您的姓名 *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      maxLength={50}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      placeholder="张三"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      联系电话 *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      maxLength={20}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      placeholder="13800000000"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      maxLength={100}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      placeholder="(可选) zhangsan@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                      公司名称
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      maxLength={100}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      placeholder="(可选)"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-slate-700 mb-2">
                    咨询类型 *
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                  >
                    <option value="">请选择咨询类型</option>
                    <option value="product">产品咨询</option>
                    <option value="technical">技术支持</option>
                    <option value="business">商务合作</option>
                    <option value="other">其他问题</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    消息内容 *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                    placeholder="请详细描述您的问题或需求，我们将更快地为您提供解决方案..."
                  />
                  <div className="text-right text-xs text-slate-400 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      发送中...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      发送消息
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* 底部导航 */}
      <footer className="bg-slate-800 text-white py-8 mt-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* 左侧：Logo和品牌信息 */}
            <div className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto"
              />
              <div>
                <div className="font-semibold text-lg">{siteName}</div>
                <div className="text-xs text-slate-400">让每一笔业务都有据可查</div>
              </div>
            </div>

            {/* 右侧：导航链接 */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <a href="/terms" className="hover:text-white transition-colors">服务条款</a>
              <a href="/privacy" className="hover:text-white transition-colors">隐私政策</a>
              <a href="/help" className="hover:text-white transition-colors">帮助中心</a>
              <a href="/contact" className="hover:text-white transition-colors">联系我们</a>
              <a href="/about" className="hover:text-white transition-colors">关于我们</a>
            </div>
          </div>

          {/* 底部备案信息 */}
          <div className="border-t border-slate-700 mt-8 pt-6 text-center text-xs text-slate-500">
            <SiteFiling />
          </div>
        </div>
      </footer>
    </div>
  );
}
